import { isGroupEnd, isGroupStart, isNotOperator } from './query-manipulation/utils';
import { RecentSearch, SearchQueryTermType, SearchQueryTermWithoutPositions } from './types';

export const MAX_SHOWN_RECENT_SEARCH_RESULTS = 15;

function isTermIncluded(
  term: SearchQueryTermWithoutPositions,
  recentSearchTerm: SearchQueryTermWithoutPositions,
): boolean {
  return recentSearchTerm.value.includes(term.value) && recentSearchTerm.type === term.type;
}

function termMatches(
  term: SearchQueryTermWithoutPositions,
  recentSearchTerms: SearchQueryTermWithoutPositions[],
): boolean {
  return recentSearchTerms.some((recentSearchTerm) => isTermIncluded(term, recentSearchTerm));
}

function allTermsMatch(
  currentTerms: SearchQueryTermWithoutPositions[],
  recentSearchTerms: SearchQueryTermWithoutPositions[],
): boolean {
  return currentTerms.every((term) => termMatches(term, recentSearchTerms));
}

function removeGroups(terms: SearchQueryTermWithoutPositions[]): SearchQueryTermWithoutPositions[] {
  return terms.filter((term) => !isGroupStart(term) && !isGroupEnd(term));
}

function removeNotOperators(
  terms: SearchQueryTermWithoutPositions[],
): SearchQueryTermWithoutPositions[] {
  return terms.filter((term) => !isNotOperator(term));
}

function keepCriteria(terms: SearchQueryTermWithoutPositions[]): SearchQueryTermWithoutPositions[] {
  return terms.filter((term) => term.type === SearchQueryTermType.CRITERION);
}

function getRank(
  currentTerms: SearchQueryTermWithoutPositions[],
  recentSearchTerms: SearchQueryTermWithoutPositions[],
): number | null {
  if (allTermsMatch(currentTerms, recentSearchTerms)) {
    return 1;
  }

  const currentTermsWithoutNot = removeNotOperators(currentTerms);
  const recentTermsWithoutNot = removeNotOperators(recentSearchTerms);

  if (allTermsMatch(currentTermsWithoutNot, recentTermsWithoutNot)) {
    return 2;
  }

  const currentCriteria = keepCriteria(currentTerms);
  const recentCriteria = keepCriteria(recentSearchTerms);

  if (allTermsMatch(currentCriteria, recentCriteria)) {
    return 3;
  }

  return null;
}

export function findSearchesMatchingTerms(
  currentTerm: SearchQueryTermWithoutPositions[],
  recentSearches: RecentSearch[],
  config = {
    maxShownRecentSearchResults: MAX_SHOWN_RECENT_SEARCH_RESULTS,
  },
): RecentSearch[] {
  const currentTermWithoutGroups = removeGroups(currentTerm);
  const rankedById = new Map<string, { rank: number; recentSearch: RecentSearch }>();

  recentSearches.forEach((recentSearch) => {
    const recentSearchTermsWithoutGroups = removeGroups(recentSearch.terms);
    const rank = getRank(currentTermWithoutGroups, recentSearchTermsWithoutGroups);

    if (rank == null) {
      return;
    }

    const existing = rankedById.get(recentSearch.id);

    if (!existing || rank < existing.rank) {
      rankedById.set(recentSearch.id, { rank, recentSearch });
    }
  });

  return Array.from(rankedById.values())
    .sort((a, b) => a.rank - b.rank)
    .map(({ recentSearch }) => recentSearch)
    .slice(0, config.maxShownRecentSearchResults);
}
